import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { TeamRankingHistoryEntry } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, query, where } from "firebase/firestore";

export interface TeamRankingHistoryDocument extends TeamRankingHistoryEntry {
  id: string;
  teamId: string;
  tournamentId: string;
  matchId: string;
  createdAt: Date;
}

/**
 * Hook for fetching team ranking history
 * Fetches all ranking changes for a team from the teamRankingHistory collection
 *
 * @param teamId - The ID of the team
 * @returns TanStack Query result with ranking history array
 */
export function useTeamRankingHistory(teamId: string | undefined) {
  return useQuery({
    queryKey: ["teamRankingHistory", teamId],
    queryFn: async () => {
      if (!teamId) return [];

      const historyQuery = query(
        collection(db, "teamRankingHistory"),
        where("teamId", "==", teamId),
      );

      const snapshot = await getDocs(historyQuery);
      return snapshot.docs.map((doc) =>
        formatFirestoreDocument(doc),
      ) as TeamRankingHistoryDocument[];
    },
    enabled: !!teamId,
    // staleTime: 1000 * 60 * 5, // 5 minutes cache
    staleTime: 0,
  });
}
